import moment from 'moment-timezone';
import { CommunicationChannel } from '../../types/communications';
import { PeriodType } from '../../types/deliveryControl';
import { friendlyName } from '../../types/field';
import {
    DayOfWeek,
    DifferenceOperator,
    type DateTimeConfig,
} from '../../types/journeys';

export const truncateAtWordBoundary = (
    value: string,
    maxChars: number,
): string => {
    const trimmed = value.trim().replace(/\s+/g, ' ');
    if (trimmed.length <= maxChars) return trimmed;

    const cut = trimmed.slice(0, maxChars);
    const lastSpace = cut.lastIndexOf(' ');
    if (lastSpace > 10) {
        return cut.slice(0, lastSpace).trim();
    }
    return cut.trim();
};

const UUID_PATTERN =
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

export const stripUuidLikeTokens = (value: string): string =>
    value.replace(UUID_PATTERN, '').replace(/\s+/g, ' ').trim();

/** `{{first_name}}`, `{% if %}`, `${amount}` — templating that must never seed a name. */
const MERGE_TAG_PATTERN = /\{\{[^}]*\}\}|\{%[^%]*%\}|\$\{[^}]*\}/g;
const HTML_ENTITY_PATTERN = /&(?:[a-z]+|#\d+);/gi;
const EMOJI_PATTERN = /[\p{Extended_Pictographic}️]/gu;
const REPLY_PREFIX_PATTERN = /^(?:re|fwd|fw)\s*:\s*/i;
const LEADING_PUNCTUATION_PATTERN = /^[\s\-–—,:;.!?"'“”‘’]+/;
const TRAILING_PUNCTUATION_PATTERN = /[\s\-–—,:;."'“”‘’]+$/;

/**
 * Makes copy written for end users safe to seed a name with: subject lines and
 * preview text routinely carry merge tags, emoji, entities and reply prefixes.
 */
export const sanitizeHumanText = (value: string): string => {
    let text = value
        .replace(MERGE_TAG_PATTERN, ' ')
        .replace(HTML_ENTITY_PATTERN, ' ')
        .replace(EMOJI_PATTERN, ' ');

    // Reply prefixes stack: "Re: Fwd: Your order".
    let previous = '';
    while (previous !== text) {
        previous = text;
        text = text.trim().replace(REPLY_PREFIX_PATTERN, '');
    }

    return (
        text
            .replace(/\s+/g, ' ')
            // Removing a merge tag strands its punctuation: "Hi , your order".
            .replace(/\s+([,;:.!?])/g, '$1')
            .replace(/([,;:])\1+/g, '$1')
            .replace(LEADING_PUNCTUATION_PATTERN, '')
            .replace(TRAILING_PUNCTUATION_PATTERN, '')
            .trim()
    );
};

const periodTypeToUnitMs: Record<PeriodType, number> = {
    SECOND: 1000,
    MINUTE: 60_000,
    HOUR: 3_600_000,
    DAY: 86_400_000,
    WEEK: 604_800_000,
    MONTH: 2_592_000_000, // approx 30 days
};

const resolveDurationUnit = (
    durationMs: number,
    unit?: PeriodType,
): PeriodType => {
    if (unit) return unit;
    if (durationMs >= periodTypeToUnitMs[PeriodType.MONTH])
        return PeriodType.MONTH;
    if (durationMs >= periodTypeToUnitMs[PeriodType.WEEK])
        return PeriodType.WEEK;
    if (durationMs >= periodTypeToUnitMs[PeriodType.DAY]) return PeriodType.DAY;
    if (durationMs >= periodTypeToUnitMs[PeriodType.HOUR])
        return PeriodType.HOUR;
    if (durationMs >= periodTypeToUnitMs[PeriodType.MINUTE])
        return PeriodType.MINUTE;
    return PeriodType.SECOND;
};

export const humanizeDuration = (
    durationMs: number,
    unit?: PeriodType,
): string => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return '0 minutes';

    const effectiveUnit = resolveDurationUnit(durationMs, unit);
    const unitMs = periodTypeToUnitMs[effectiveUnit];
    const value = Math.max(1, Math.round(durationMs / unitMs));
    const plural = value === 1 ? '' : 's';

    return `${value} ${effectiveUnit.toLowerCase()}${plural}`;
};

const FIELD_ID_PREFIXES = new Set(['FCT', 'DIM', 'FACT', 'SRT', 'TBL']);

/**
 * Canonical channel labels for naming context. Singular Title Case form —
 * consumers that need lowercase or plural derive from this.
 */
export const CHANNEL_LABELS: Record<CommunicationChannel, string> = {
    [CommunicationChannel.EMAIL]: 'Email',
    [CommunicationChannel.SMS]: 'SMS',
    [CommunicationChannel.WHATSAPP]: 'WhatsApp',
    [CommunicationChannel.PUSH]: 'Push',
    [CommunicationChannel.SLACK]: 'Slack',
    [CommunicationChannel.ANY]: 'Any',
    [CommunicationChannel.UNKNOWN]: 'Unknown',
};

/**
 * Trailing random id segments like Ivfbf3cj / xyz98765 / a1B2-c3d4 on
 * additionalMetrics names. nanoid's alphabet is A-Za-z0-9_-, but words are
 * already split on `_`, so only `-` needs accepting inside a segment.
 */
const isRandomIdSegment = (segment: string): boolean => {
    if (segment.length < 8 || segment.length > 12) return false;
    if (!/^[A-Za-z0-9-]+$/u.test(segment)) return false;
    const hasLetter = /[A-Za-z]/u.test(segment);
    const hasDigit = /\d/u.test(segment);
    return hasLetter && hasDigit;
};

/** True when a URL path segment is an opaque id, not a human purpose word. */
export const isOpaqueIdSegment = (segment: string): boolean => {
    const trimmed = segment.trim();
    if (!trimmed) return true;
    if (/^\d+$/u.test(trimmed)) return true;
    if (trimmed.startsWith('eyJ')) return true;
    if (
        trimmed.length >= 24 &&
        /^[A-Za-z0-9._-]+$/u.test(trimmed) &&
        /[A-Za-z]/u.test(trimmed) &&
        /\d/u.test(trimmed)
    ) {
        return true;
    }
    // Reset lastIndex — UUID_PATTERN carries /g.
    UUID_PATTERN.lastIndex = 0;
    if (UUID_PATTERN.test(trimmed)) {
        UUID_PATTERN.lastIndex = 0;
        return true;
    }
    UUID_PATTERN.lastIndex = 0;
    return isRandomIdSegment(trimmed);
};

const humanizeToken = (text: string): string => {
    const trimmed = text.trim();
    if (!trimmed) return '';
    return friendlyName(trimmed);
};

export const humanizeFilterFieldId = (
    fieldId: string,
    labelByFieldId?: Record<string, string>,
): string => {
    const trimmed = fieldId.trim();
    if (!trimmed) return '';

    const fromMap = labelByFieldId?.[trimmed]?.trim();
    if (fromMap) return humanizeToken(fromMap);

    const doubleUnderscoreParts = trimmed.split('__').filter(Boolean);
    const withoutPrefixes = doubleUnderscoreParts.filter(
        (part) => !FIELD_ID_PREFIXES.has(part.toUpperCase()),
    );
    const meaningfulParts =
        withoutPrefixes.length > 0 ? withoutPrefixes : doubleUnderscoreParts;
    const lastSegment = meaningfulParts[meaningfulParts.length - 1] ?? trimmed;

    let words = lastSegment
        .split(/[_\s]+/)
        .map((word) => word.trim())
        .filter(Boolean)
        .filter((word) => !FIELD_ID_PREFIXES.has(word.toUpperCase()));

    // Drop trailing random metric id suffixes before humanizing.
    while (words.length > 1 && isRandomIdSegment(words[words.length - 1])) {
        words = words.slice(0, -1);
    }

    // If the only remaining token is a random id, treat as unresolvable.
    if (words.length === 1 && isRandomIdSegment(words[0])) {
        return '';
    }

    if (words.length === 0) return '';

    // Prefer the last word for noisy warehouse ids like PLATFORM_ACCOUNTS_PLATFORM.
    let labelSource = words;
    if (words.length > 2) {
        labelSource = [words[words.length - 1]];
    } else if (
        words.length === 2 &&
        words[0].toLowerCase() === words[1].toLowerCase()
    ) {
        labelSource = [words[1]];
    }

    return humanizeToken(labelSource.join(' '));
};

export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
    [DayOfWeek.SUNDAY]: 'Sunday',
    [DayOfWeek.MONDAY]: 'Monday',
    [DayOfWeek.TUESDAY]: 'Tuesday',
    [DayOfWeek.WEDNESDAY]: 'Wednesday',
    [DayOfWeek.THURSDAY]: 'Thursday',
    [DayOfWeek.FRIDAY]: 'Friday',
    [DayOfWeek.SATURDAY]: 'Saturday',
};

export const humanizeEventName = (eventName: string): string =>
    humanizeToken(eventName.replace(/[_-]+/g, ' '));

const stripDynamicFieldPrefix = (value: string): string =>
    value.replace(/^\$\./, '').trim();

export const humanizeDateTimeValue = (value: string | number): string => {
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return '';
        const ms = value > 1_000_000_000_000 ? value : value * 1000;
        const date = new Date(ms);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toISOString().slice(0, 10);
    }

    const trimmed = value.trim();
    if (!trimmed) return '';

    const withoutDynamic = stripDynamicFieldPrefix(trimmed);
    if (withoutDynamic !== trimmed || /__/u.test(withoutDynamic)) {
        return humanizeFilterFieldId(withoutDynamic) || withoutDynamic;
    }

    if (/^\d{4}-\d{2}-\d{2}/u.test(trimmed)) {
        return trimmed.slice(0, 10);
    }

    return humanizeFilterFieldId(trimmed) || humanizeToken(trimmed);
};

/**
 * Fixed wait-till-date values are stored in UTC (ISO + optional HH:mm) and shown
 * in the workspace timezone in the UI. Format supporting naming signals the same
 * way so descriptions match the config panel.
 */
export const formatAbsoluteWaitDateTimeForNaming = (
    value: string | number,
    time: string | undefined,
    workspaceTimezone: string,
): { dateLabel: string; timeLabel: string | undefined } => {
    let utcMoment: moment.Moment;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return { dateLabel: '', timeLabel: undefined };
        }
        const ms = value > 1_000_000_000_000 ? value : value * 1000;
        utcMoment = moment.utc(ms);
    } else {
        const trimmed = value.trim();
        if (!trimmed) {
            return { dateLabel: '', timeLabel: undefined };
        }
        utcMoment = moment.utc(trimmed);
    }

    if (!utcMoment.isValid()) {
        return { dateLabel: '', timeLabel: undefined };
    }

    const trimmedTime = time?.trim();
    if (trimmedTime) {
        const [hours, minutes] = trimmedTime.split(':').map(Number);
        if (Number.isFinite(hours) && Number.isFinite(minutes)) {
            utcMoment.hours(hours).minutes(minutes).seconds(0).milliseconds(0);
        }
    }

    const local = utcMoment.tz(workspaceTimezone);
    return {
        dateLabel: local.format('D MMM YYYY'),
        timeLabel: trimmedTime ? local.format('HH:mm') : undefined,
    };
};

export const humanizeTimeDifference = (
    timeDifference: DateTimeConfig['timeDifference'] | undefined,
): string => {
    if (!timeDifference) return '';
    const { value, operator } = timeDifference;
    if (!Number.isFinite(value) || value === 0) return '';
    const abs = Math.abs(value);
    const unit = abs === 1 ? 'day' : 'days';
    if (operator === DifferenceOperator.SUBTRACT) {
        return `${abs} ${unit} before`;
    }
    return `${abs} ${unit} after`;
};

/** Internal — used by signals.ts. Not part of the public journeyNaming surface. */
export { humanizeToken };
